import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDrives, useSetPublishTarget } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { DriveSafe } from '@vivport/backend/types';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  DRIVE_FOLDER_ROOT_CRUMB,
  DriveFolderPicker,
  driveFolderCrumbsPath,
  type DriveFolderCrumb,
} from './DriveFolderPicker';

type PublishFolderDialogProps = {
  memberId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const PROBE_HINTS: Partial<Record<DriveSafe['sourceKind'], string>> = {
  MICROSOFT:
    'If this fails with an access error, the SharePoint admin has not yet granted Vivport write on this site: see the Microsoft 365 integration page for the command.',
  GOOGLE: 'If this fails, reconnect the Google Drive source to grant the file scope.',
};

export function PublishFolderDialog({ memberId, open, onOpenChange }: PublishFolderDialogProps) {
  const drives = useDrives(memberId);
  const setTarget = useSetPublishTarget();
  const [driveId, setDriveId] = useState<number | null>(null);
  const [crumbs, setCrumbs] = useState<DriveFolderCrumb[]>([DRIVE_FOLDER_ROOT_CRUMB]);

  const list = drives.data?.drives ?? [];
  const drive = list.find((candidate) => candidate.id === driveId) ?? null;
  const path = driveFolderCrumbsPath(crumbs);
  const hint = drive ? PROBE_HINTS[drive.sourceKind] : undefined;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setDriveId(null);
      setCrumbs([DRIVE_FOLDER_ROOT_CRUMB]);
      setTarget.reset();
    }
    onOpenChange(next);
  };

  const selectDrive = (value: string) => {
    setDriveId(Number(value));
    setCrumbs([DRIVE_FOLDER_ROOT_CRUMB]);
    setTarget.reset();
  };

  const handleConfirm = () => {
    if (!drive) {
      return;
    }
    setTarget.mutate(
      { driveId: drive.id, parentProviderItemId: crumbs[crumbs.length - 1].id },
      {
        onSuccess: (target) => {
          toast.success(`Publishing to ${target.driveName} at ${target.rootPath}.`);
          handleOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose the publish folder</DialogTitle>
          <DialogDescription>
            Pick a drive and the folder Vivport publishes this Hub&apos;s projects into.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <div className="space-y-2">
            <Label>Drive</Label>
            {drives.isLoading ? <p className="text-sm text-muted-foreground">Loading drives...</p> : null}
            {drives.isError ? (
              <p className="text-sm text-destructive">{getAxiosErrorDisplayMessage(drives.error, 'Failed to load drives.')}</p>
            ) : null}
            {!drives.isLoading && !drives.isError && list.length === 0 ? (
              <p className="text-sm text-muted-foreground">No drives are configured for this Hub yet.</p>
            ) : null}
            <RadioGroup value={driveId === null ? '' : String(driveId)} onValueChange={selectDrive}>
              {list.map((candidate) => {
                const readOnly = candidate.writable === false;
                return (
                  <div key={candidate.id} className="flex items-center space-x-2">
                    <RadioGroupItem value={String(candidate.id)} id={`publish-drive-${candidate.id}`} disabled={readOnly} />
                    <Label
                      htmlFor={`publish-drive-${candidate.id}`}
                      className={readOnly ? 'text-muted-foreground' : undefined}
                    >
                      {candidate.name}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {readOnly ? 'read-only on the relay' : candidate.productLabel}
                      </span>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {drive ? (
            <div className="space-y-2">
              <Label>Folder</Label>
              <DriveFolderPicker key={drive.id} driveId={drive.id} crumbs={crumbs} onCrumbsChange={setCrumbs} />
              <p className="text-sm">Vivport will create a &apos;Vivport&apos; folder in {path}</p>
              {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
            </div>
          ) : null}

          {setTarget.isError ? (
            <p className="text-sm text-destructive">
              {getAxiosErrorDisplayMessage(setTarget.error, 'Failed to create the publish folder.')}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!drive || setTarget.isPending}>
            {setTarget.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
